import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';

import Icon from 'kingpin/atoms/Icon';
import PopUpMenu from 'kingpin/atoms/PopUpMenu';
import UserAvatar from 'kingpin/atoms/Avatar';
import Typography from 'kingpin/atoms/Typography';
import AccountContext from 'contexts/AccountContext';
import AccountPickerContext from 'contexts/AccountPickerContext';
import CurrentUserContext from 'contexts/CurrentUserContext';
import AccountPicker from 'components/AccountPicker';
import useDarkModeToggle from 'theme/useDarkModeToggle';

import NavIconItem from './NavIconItem';
import NavMenuPopup from '../Standard/TopBar/Left/NavMenuPopup';
import { ContentLink } from '../Standard/TopBar/Left/ContentMenu';

const PopupDiv = styled.div`
  width: 293px;
  border-radius: 16px;
  padding: 16px 12px;
  user-select: none;

  box-shadow: var(--shadow-lg);
`;

const NameAndAccountDiv = styled.div`
  padding: 16px 8px;
  display: flex;
  gap: 8px;
  flex-direction: column;
  align-items: center;
`;

const NameText = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0;
  vertical-align: middle;
  color: var(--color-text-primary);
`;

const AccountText = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 13px;
  line-height: 24px;
  letter-spacing: 0;
  vertical-align: middle;
  color: var(--color-icon-secondary);
`;

const CarrierText = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  letter-spacing: 0;
  vertical-align: middle;
  max-width: 165px;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--color-icon-primary);
`;

const ThemeSliderTrack = styled.div`
  width: 64px;
  height: 32px;
  border-radius: 10px;
  background-color: var(--color-bg-secondary);
  position: relative;
  display: flex;
  flex-shrink: 0;
`;

const ThemeSliderIndicator = styled.div<{ index: number }>`
  position: absolute;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background-color: var(--color-bg-primary);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  left: ${(p) => p.index * 32}px;
  transition: left 0.2s cubic-bezier(0.16, 1, 0.3, 1);
`;

const ThemeSliderOption = styled.button<{ isActive: boolean }>`
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  color: ${(p) =>
    p.isActive ? 'var(--color-text-primary)' : 'var(--color-icon-secondary)'};
  transition: color 0.2s ease;
  padding: 0;
`;

const THEME_OPTIONS: { mode: 'light' | 'dark'; icon: Kingpin.Icon }[] = [
  { mode: 'light', icon: 'sun' },
  { mode: 'dark', icon: 'moon' },
];

const ThemeSlider = () => {
  const { isDarkMode, setThemeMode } = useDarkModeToggle();
  const activeIndex = isDarkMode ? 1 : 0;

  return (
    <ThemeSliderTrack>
      <ThemeSliderIndicator index={activeIndex} />
      {THEME_OPTIONS.map(({ mode, icon }) => (
        <ThemeSliderOption
          key={mode}
          data-testid={`theme-${mode}`}
          isActive={isDarkMode === (mode === 'dark')}
          onClick={() => setThemeMode(mode)}
        >
          <Icon icon={icon} width={16} height={16} color={'currentColor'} />
        </ThemeSliderOption>
      ))}
    </ThemeSliderTrack>
  );
};

const useInitials = () => {
  const { displayName } = useContext(CurrentUserContext);
  const initials = useMemo(() => {
    try {
      return displayName
        .trim()
        .split(' ')
        .map((word) => word[0])
        .map((letter) => letter.toUpperCase())
        .join('');
    } catch (ex) {
      return '';
    }
  }, [displayName]);

  return initials;
};

const Profile = () => {
  const { carrierName } = useContext(AccountContext);
  const { accounts } = useContext(AccountPickerContext);
  const { displayName } = useContext(CurrentUserContext);
  const initials = useInitials();

  return (
    <PopUpMenu
      trigger={() => (
        <NavIconItem
          style={{
            border:
              accounts.length <= 1
                ? 'none'
                : '1px solid var(--color-border-primary)',
            paddingRight: 12,
            borderRadius: 24,
            padding: '3px 4px 3px 12px',
          }}
        >
          <CarrierText>{carrierName}</CarrierText>

          <UserAvatar size={'topbar'} colorKey={'blue'} initials={initials} />
        </NavIconItem>
      )}
      children={() => (
        <PopupDiv>
          <NameAndAccountDiv>
            <UserAvatar initials={initials} size={'large'} colorKey={'blue'} />
            <div>
              <div>
                <NameText>{displayName}</NameText>
              </div>
              {accounts.length <= 1 && (
                <div>
                  <AccountText>{carrierName}</AccountText>
                </div>
              )}
            </div>
            {accounts.length > 1 && (
              <div style={{ width: '100%' }}>
                <AccountPicker />
              </div>
            )}
          </NameAndAccountDiv>
          <NavMenuPopup.Divider />
          <div
            style={{
              height: 36,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: 8,
            }}
          >
            <Typography.Body type={'Label 14'}>Appearance</Typography.Body>
            <div style={{ marginLeft: 'auto' }}>
              <ThemeSlider />
            </div>
          </div>
          <NavMenuPopup.Divider />
          <ContentLink label={'Manage Account'} to={'/my-account'} />
          <ContentLink label={'Log Out'} to={'/signOut'} />
        </PopupDiv>
      )}
      position={'bottom-end'}
    />
  );
};

export default Profile;
